import { Spinner } from '@/components/ui/spinner';
// Analysis progress screen. While the simulated job runs it shows the PMS
// spinner + a progress bar; "Commencer la revue" enables only at 100%. Mounting
// this screen marks the session as "watched" so its completion enables the
// button in place instead of raising a toast (the toast is for off-screen
// completion only).
import { WordShellTopbar } from '@/components/words/word-shell-topbar';
import { accentStyle } from '@/lib/bulk-add/accent';
import { useBulkAdd } from '@/lib/bulk-add/bulk-session';
import type { BulkSession } from '@/lib/bulk-add/types';
import { useEffect } from 'react';

interface BulkLoadingProps {
  session: BulkSession;
}

/** Live progress screen for a session's analysis job. */
export function BulkLoading({ session }: BulkLoadingProps) {
  const bulk = useBulkAdd();
  const ready = session.status === 'ready';

  // Tell the provider this session is being watched, so completion enables the
  // button here rather than firing a toast. `setWatching` is stable, so this
  // runs only on mount/unmount (and id change), not on every progress tick.
  const { setWatching } = bulk;
  useEffect(() => {
    setWatching(session.id);
    return () => setWatching(null);
  }, [setWatching, session.id]);

  const pct = ready
    ? 100
    : session.total > 0
      ? Math.round((session.processed / session.total) * 100)
      : 0;

  return (
    <>
      <WordShellTopbar />
      <div className="bulk" style={accentStyle(session.noyau)}>
        <div className="bulk-loading">
          <Spinner size="lg" label="Analyse en cours" />
          <h1 className="bulk-loading__title">
            {ready ? 'Analyse terminée' : 'Analyse en cours…'}
          </h1>
          <p className="bulk-loading__count" aria-live="polite">
            {ready
              ? `${session.total} mot${session.total > 1 ? 's' : ''} prêt${session.total > 1 ? 's' : ''} à réviser`
              : `${session.processed} / ${session.total} mots analysés`}
          </p>

          <progress
            className="bulk-progress"
            value={pct}
            max={100}
            aria-label="Progression de l'analyse"
          />

          <button
            type="button"
            className="bulk-btn bulk-btn--primary"
            disabled={!ready}
            onClick={() => bulk.beginReview(session.id)}
          >
            Commencer la revue
          </button>
        </div>
      </div>
    </>
  );
}
